// From https://gist.github.com/lazaronixon/dca1b48c241422d6347f4b0c93bec739
import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"
import Dropzone from "dropzone"
import { getMetaValue, findElement, removeElement, insertAfter } from "helpers"

Dropzone.autoDiscover = false

export default class extends Controller {
  static targets = ["input"]

  connect() {
    this.dropZone = createDropZone(this)
    this.hideFileInput()
    this.bindEvents()
    this.displayExistingFiles()
  }

  // Private
  hideFileInput() {
    this.inputTarget.disabled = true
    this.inputTarget.style.display = "none"
  }

  bindEvents() {
    this.dropZone.on("addedfile", (file) => {
      if (file.attachmentId) return

      if (this.maxFiles == 1 && this.dropZone.files[1]) {
        this.dropZone.removeFile(this.dropZone.files[0])
      }
      setTimeout(() => {
        file.accepted && createDirectUploadController(this, file).start()
      }, 500)
    })

    this.dropZone.on("removedfile", (file) => {
      file.controller && removeElement(file.controller.hiddenInput)

      const detachUrl = this.data.get("detachUrl")
      if (detachUrl && file.attachmentId) {
        const csrfToken = getMetaValue("csrf-token")
        fetch(`${detachUrl}?attachment_id=${file.attachmentId}`, {
          method: "DELETE",
          headers: { "X-CSRF-Token": csrfToken },
        })
      }
    })

    this.dropZone.on("canceled", (file) => {
      file.controller && file.controller.xhr.abort()
    })

    this.dropZone.on("processing", (file) => {
      this.submitButton.disabled = true
    })

    this.dropZone.on("queuecomplete", (file) => {
      this.submitButton.disabled = false
    })
  }

  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") }
  }

  get url() {
    return this.inputTarget.getAttribute("data-direct-upload-url")
  }

  get maxFiles() {
    return this.data.get("maxFiles") || 1
  }

  get maxFileSize() {
    return this.data.get("maxFileSize") || 256
  }

  get acceptedFiles() {
    return this.data.get("acceptedFiles")
  }

  get addRemoveLinks() {
    return this.data.get("addRemoveLinks") || false
  }

  get existingFiles() {
    const data = this.data.get("existingFiles")
    return data ? JSON.parse(data) : []
  }

  displayExistingFiles() {
    this.existingFiles.forEach((fileInfo) => {
      const mockFile = {
        name: fileInfo.filename,
        size: fileInfo.byte_size,
        attachmentId: fileInfo.attachment_id,
        status: Dropzone.SUCCESS,
        accepted: true,
      }
      this.dropZone.emit("addedfile", mockFile)
      this.dropZone.emit("complete", mockFile)
      this.dropZone.files.push(mockFile)
    })
  }

  get form() {
    return this.element.closest("form")
  }

  get submitButton() {
    return findElement(this.form, "input[type=submit], button[type=submit]")
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this)
    this.source = source
    this.file = file
  }

  start() {
    this.file.controller = this
    this.hiddenInput = this.createHiddenInput()
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput)
        this.emitDropzoneError(error)
      } else {
        this.hiddenInput.value = attributes.signed_id
        this.emitDropzoneSuccess()
      }
    })
  }

  // Private
  createHiddenInput() {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.source.inputTarget.name
    insertAfter(input, this.source.inputTarget)
    return input
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr)
    this.emitDropzoneUploading()
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr
    this.xhr.upload.addEventListener("progress", (event) =>
      this.uploadRequestDidProgress(event),
    )
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element
    const progress = (event.loaded / event.total) * 100
    findElement(
      this.file.previewTemplate,
      ".dz-upload",
    ).style.width = `${progress}%`
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING
    this.source.dropZone.emit("processing", this.file)
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR
    this.source.dropZone.emit("error", this.file, error)
    this.source.dropZone.emit("complete", this.file)
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS
    this.source.dropZone.emit("success", this.file)
    this.source.dropZone.emit("complete", this.file)

    const attachUrl = this.source.data.get("attachUrl")
    if (attachUrl) {
      const csrfToken = getMetaValue("csrf-token")
      const formData = new FormData()
      formData.append("signed_id", this.hiddenInput.value)

      fetch(attachUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: formData,
      }).then((response) => {
        if (response.ok) {
          const attachmentId = response.headers.get("X-Attachment-Id")
          if (attachmentId) {
            this.file.attachmentId = attachmentId
          }
        }
      })
    }
  }
}

// Top level...
function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file)
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller)
}

function createDropZone(controller) {
  return new Dropzone(controller.element, {
    url: controller.url,
    headers: controller.headers,
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    autoQueue: false,
    createImageThumbnails: false,
    dictRemoveFile: "Remove",
    dictCancelUpload: "Cancel",
  })
}
